import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/site/SiteLayout";
import { api } from "@/lib/api";
import { ArrowRight } from "lucide-react";
import useSeo from "@/lib/useSeo";

export function Blog() {
  useSeo({
    title: "Gift Card Buyback Blog — Guides, Rates & News | GC Nexon",
    description: "Guides, industry news, and rate analysis for selling unused gift cards. Learn how to spot the best offers, avoid scams, and maximize your payout.",
    path: "/blog",
  });
  const [posts, setPosts] = useState([]);
  useEffect(() => { api.get("/blog").then((r) => setPosts(r.data)); }, []);

  return (
    <SiteLayout>
      <section className="section-py container-x">
        <span className="inline-block px-4 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-bold uppercase mb-4">Blog</span>
        <h1 className="text-5xl md:text-6xl font-extrabold tracking-tight">Guides, rates, and stories.</h1>
        <p className="mt-4 text-muted-foreground text-lg max-w-2xl">Fresh insights from the GC Nexon team — helping you get the most from every unused gift card.</p>

        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {posts.map((p) => (
            <Link key={p.id} to={`/blog/${p.slug}`} className="card-soft hover:-translate-y-0.5 hover:shadow-md group overflow-hidden p-0" style={{ transition: "transform .2s ease, box-shadow .2s ease" }} data-testid={`blog-post-${p.slug}`}>
              {(p.hero_image || p.cover_image) ? (
                <img
                  src={p.hero_image || p.cover_image}
                  alt=""
                  className="w-full aspect-[16/9] object-cover bg-secondary/40"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              ) : null}
              <div className="p-6">
                <div className="text-xs text-primary uppercase font-bold">{p.category || p.tags?.[0] || "Guide"}</div>
                <h3 className="mt-2 font-extrabold text-xl leading-tight group-hover:text-primary">{p.title}</h3>
                <p className="mt-3 text-sm text-muted-foreground line-clamp-3">{p.excerpt}</p>
                <div className="mt-4 text-sm font-bold text-primary flex items-center">Read more <ArrowRight size={14} className="ml-1" /></div>
              </div>
            </Link>
          ))}
        </div>
        {posts.length === 0 && (
          <div className="mt-14 rounded-2xl border border-dashed border-border p-12 text-center text-muted-foreground">
            <p>No posts yet — check back soon.</p>
          </div>
        )}
      </section>
    </SiteLayout>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);
  useEffect(() => { api.get(`/blog/${slug}`).then((r) => setPost(r.data)).catch(() => setNotFound(true)); }, [slug]);
  useSeo({
    title: post ? `${post.title} — GC Nexon` : "Loading… — GC Nexon",
    description: post?.excerpt || post?.title || "Read the latest article on selling gift cards for cash.",
    path: `/blog/${slug}`,
    image: post?.hero_image || post?.cover_image,
  });
  if (notFound) return <SiteLayout><section className="section-py container-x max-w-2xl"><h1 className="text-3xl font-extrabold">Post not found</h1><Link to="/blog" className="btn-pill mt-6 inline-flex text-sm">Back to blog</Link></section></SiteLayout>;
  if (!post) return null;

  const html = post.body_html || markdownToHtml(post.content || "");

  return (
    <SiteLayout>
      <article className="section-py container-x max-w-3xl">
        <div className="text-xs text-primary uppercase font-bold">{post.category || post.tags?.[0] || "Guide"}</div>
        <h1 className="mt-3 text-4xl md:text-5xl font-extrabold tracking-tight">{post.title}</h1>
        <div className="mt-4 text-sm text-muted-foreground flex items-center gap-3">
          <span>{new Date(post.published_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}</span>
          {post.read_time ? <><span>·</span><span>{post.read_time} min read</span></> : null}
        </div>
        {(post.hero_image || post.cover_image) && (
          <img
            src={post.hero_image || post.cover_image}
            alt=""
            className="mt-8 w-full rounded-2xl border border-border"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        )}
        <div className="mt-10 prose prose-lg max-w-none prose-headings:font-extrabold prose-headings:tracking-tight prose-a:text-primary" dangerouslySetInnerHTML={{ __html: html }} />
        <div className="mt-14 border-t border-border pt-8">
          <Link to="/blog" className="btn-pill-outline text-sm">← All posts</Link>
        </div>
      </article>
    </SiteLayout>
  );
}

// Tiny markdown → HTML fallback (headings, bold, italics, links, lists, paragraphs).
// Used only if the API doesn't return body_html.
function markdownToHtml(md) {
  if (!md) return "";
  const lines = md.split(/\r?\n/);
  const out = [];
  let inList = false;
  const flushList = () => { if (inList) { out.push("</ul>"); inList = false; } };
  const inline = (s) =>
    s
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
      .replace(/\*(.+?)\*/g, "<em>$1</em>")
      .replace(/`([^`]+)`/g, "<code>$1</code>")
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>');
  for (const raw of lines) {
    const line = raw.trimEnd();
    if (!line.trim()) { flushList(); continue; }
    let m;
    if ((m = line.match(/^###\s+(.*)$/))) { flushList(); out.push(`<h3>${inline(m[1])}</h3>`); continue; }
    if ((m = line.match(/^##\s+(.*)$/))) { flushList(); out.push(`<h2>${inline(m[1])}</h2>`); continue; }
    if ((m = line.match(/^#\s+(.*)$/))) { flushList(); out.push(`<h1>${inline(m[1])}</h1>`); continue; }
    if ((m = line.match(/^[-*]\s+(.*)$/))) {
      if (!inList) { out.push("<ul>"); inList = true; }
      out.push(`<li>${inline(m[1])}</li>`);
      continue;
    }
    flushList();
    out.push(`<p>${inline(line)}</p>`);
  }
  flushList();
  return out.join("\n");
}
